<template>
  <loader v-if="loading"
    :label="$t('conflict.searching')"
  />
  <v-container
    v-else
    fluid
  >
    <span v-if="conflicts.length <= 0" class="text-center">
      <v-row>
        <v-col>
          <v-toolbar
            class="mx-6 my-3 elevation-2"
            color="#E0F2F1"
          >
            <template>
              <span class="text-h6">{{ $t('conflict.noResolutionToDo') }}</span>
              <v-spacer/>
              <span>
                <v-icon color="success">mdi-check</v-icon>
              </span>
            </template>
          </v-toolbar>
        </v-col>
      </v-row>
    </span>
    <span v-else>
      <v-row>
        <v-col>
          <v-data-iterator
            :items="conflicts"
            :items-per-page.sync="itemsPerPage"
            :page.sync="page"
            hide-default-footer
          >
            <template v-slot:header>
              <v-toolbar
                class="mx-6 my-3 elevation-0"
                color="#E0F2F1"
              >
                <template>
                  <span class="text-h6">{{ $t('conflict.selectAConflictToResolve') }}</span>
                  <v-spacer/>
                  <div class="px-4 py-2" v-if="$vuetify.breakpoint.mdAndUp">
                    <v-progress-circular
                      rotate="90"
                      :size="100"
                      :width="10"
                      :value="numberOfResolvedConflicts / totalNumberOfItems * 100"
                      color="success"
                      >
                      {{numberOfResolvedConflicts}} / {{ totalNumberOfItems }}
                    </v-progress-circular>
                    <span class="ml-4 text-overline">{{ $t('conflict.conflictsSolved') }}</span>
                  </div>
                </template>
              </v-toolbar>
            </template>

            <template v-slot:default="props">
              <v-row
                v-for="item in props.items"
                :key="item.name"
                cols="12"
                sm="6"
                md="4"
                lg="3"
                @click="goResolveConflict(item)"
                >
                <v-hover v-slot="{ hover }">
                  <v-col>
                    <conflict
                      :conflict="item"
                      class="elevation-1"
                      :class="{ 'elevation-4 raison-highlight clickable': hover }"
                      :show-details="debugMode"
                    />
                  </v-col>
                </v-hover>
              </v-row>
            </template>

            <template v-slot:footer>
              <v-row
                class="mt-2"
                align="center"
                justify="center"
              >
                <span class="grey--text caption">{{ $t('global.rowPerPage') }}</span>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      dark
                      text
                      color="grey"
                      class="ml-2 caption"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ itemsPerPage }}
                      <v-icon>mdi-chevron-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="(number, index) in itemsPerPageArray"
                      :key="index"
                      @click="updateItemsPerPage(number)"
                    >
                      <v-list-item-title>{{ number }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <span
                  class="mr-4 grey--text caption"
                >
                  {{ legend }}
                </span>
                <v-btn
                  fab
                  dark
                  color="#062b89"
                  class="mr-1"
                  @click="formerPage"
                  small
                  :disabled="isFirstPage"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <v-btn
                  fab
                  dark
                  color="#062b89"
                  class="ml-1"
                  @click="nextPage"
                  small
                  :disabled="isLastPage"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </v-row>
            </template>
          </v-data-iterator>
        </v-col>
      </v-row>
    </span>
  </v-container>
</template>
<script>
import Conflict from '@/components/conflictResolution/Conflict'
import Loader from '@/components/global/Loader'

import pageableMixin from '@/mixins/pageable-mixin.js'
import debugModeMixin from '@/mixins/debug-mode-mixin.js'

import conflictService from '@/services/conflicts.js'

export default ({
  name: 'ConflictsViewer',
  
  components: {
    Conflict,
    Loader,
  },
  
  mixins: [ debugModeMixin, pageableMixin ],
  
  data () {
    return {
      loading: true,
      conflicts: [],
      conflictSelected: {},
      numberOfResolvedConflicts: 0,

      page: 1,
      numberOfPages: 1,
      itemsPerPage: 5,
      itemsPerPageArray: [5, 10, 15, 20],
      isLastPage: true
    }
  },
  
  props: {
  },
  
  computed: {
    isConflictOnSameScenario() {
      return conflictService.isConflictOnSameScenario(this.conflictSelected)
    },
    conflictResolved() {
      return conflictService.isConflictResolved(this.conflictSelected)
    },
    conflictHasWorkInProgress() {
      return conflictService.hasConflictWorkInProgress(this.conflictSelected)
    }
  },

  methods: {
    findConflits() {
      this.loading = true
      conflictService.getConflicts(this.projectId, this.page, this.itemsPerPage).then(response => {
        this.conflicts = response.data.content
        this.conflictSelected = this.conflicts[0]
        this.numberOfPages = response.data.page.totalPages
        this.page = response.data.page.number + 1
        this.totalNumberOfItems = response.data.page.totalElements
        this.isLastPage = response.data.page.last
        this.numberOfResolvedConflicts = response.data.totalConflictsSolved
      }).catch(err => {
        this.$emit('raison-event', { type: 'error', messages: [err] })
      }).finally(() => {
        this.loading = false
      });
    },
    selectConflict(conflict) {
      this.answerSelected = undefined
      this.conflictSelected = conflict
    },
    nextPage() {
      if (this.page + 1 <= this.numberOfPages) {
        this.page += 1
        this.findConflits()
      }
    },
    formerPage() {
      if (this.page - 1 >= 1) {
        this.page -= 1
        this.findConflits()
      }
    },
    updateItemsPerPage(number) {
      this.itemsPerPage = number
      this.page = 1
      this.findConflits()
    },
    goResolveConflict(conflict) {
      console.log('goResolveConflict', conflict)
      this.$router.push({name: 'conflictResolution', params: { id: this.projectId, conflict: conflict.id }})
    }
  },

  created () {
    this.projectId = this.$route.params.id
    this.findConflits(this.projectId)
  }
})
</script>
<style>
</style>